import { format, startOfDay, subDays, subYears } from "date-fns";
import type { useForecastExperience } from "../forecast-experience/useForecastExperience";
import type { forecastManagementReadout } from "../forecast-experience/readout";
import { MANAGEMENT_COPY as C } from "./managementCopy";
import type { StateOfSalesV2LossSignalGroup } from "../lib/risk";
import type { ForecastL2Insights } from "../state-of-sales-v2/forecastInsights";

export type PipelineHealthData = ReturnType<typeof useForecastExperience>;
export type PipelineHealthReadout = NonNullable<ReturnType<typeof forecastManagementReadout>>;

/** Match the headline's existing rank without changing membership or input order. */
export function orderedLossGroups(groups: StateOfSalesV2LossSignalGroup[], patternOrder: ForecastL2Insights["lossPatterns"]["rows"]) {
  return patternOrder.flatMap(row => groups.filter(group => group.group === row.group && group.affectedDealCount > 0));
}

/** Props for the existing two-contribution asset. No arithmetic or mutation. */
export function coveragePositionProps(data: PipelineHealthData) {
  const targetAvailable = data.position && Number.isFinite(data.position.target) && data.position.target > 0;
  return data.position ? { position: data.position, companyCurrency: data.model.companyCurrency,
    expectedLabel: "Expected from open deals" as const, targetUnavailable: !targetAvailable, showBar: Boolean(targetAvailable) } : null;
}

export function hasSupportedForecastComparison(data: PipelineHealthData) {
  const movement = data.movement;
  return Boolean(movement?.available && movement.currentForecast === data.position?.forecast
    && movement.coverage.unverifiedDeals === 0 && movement.coverage.missingCurrentRecords === 0
    && !movement.includesFuture);
}

// Wording over existing outputs only. No new forecast, sufficiency or risk metric.
export function coverageAssessment(data: PipelineHealthData) {
  const position = data.position;
  if (!position || !Number.isFinite(position.target) || position.target <= 0) return {
    title: "Target not configured",
    detail: `${C.missingTargetOnly} ${C.futureExcluded}`,
  };
  if (position.historical) return {
    title: position.won >= position.target ? "Target reached" : "Target missed",
    detail: "Recorded wins for the completed period.",
  };
  if (!Number.isFinite(position.forecast)) return {
    title: C.coverageUnknown,
    detail: C.coverageUnknownDetail,
  };
  return {
    title: position.forecast >= position.target ? C.coverageCoveredTitle : C.coverageBelowTitle,
    detail: C.futureExcluded,
  };
}

export function creationAssessment(data: PipelineHealthData) {
  const history = data.futureHistory;
  if (history?.trend !== null && history?.trend !== undefined) return {
    title: history.trend === 1 ? "Creation pace is unchanged"
      : `${Math.round(Math.abs(history.trend - 1) * 100)}% ${history.trend > 1 ? "above" : "below"} last year's pace`,
    detail: "Latest 90 days versus the matching prior-year window. This is a historical comparison, not a target requirement.",
  };
  return {
    title: "Creation pace is not yet comparable",
    detail: history ? "The recent and matching prior-year windows do not yet support a reliable trend."
      : "Creation history could not be assessed for this selection.",
  };
}

export function creationComparisonDates(asOf: Date) {
  const end = startOfDay(asOf);
  const start = subDays(end, 90);
  const label = (from: Date, to: Date) => `${format(from, "d MMM yyyy")}–${format(subDays(to, 1), "d MMM yyyy")}`;
  return { current: label(start, end), prior: label(subYears(start, 1), subYears(end, 1)) };
}
